import { useEffect, useState } from 'react';

export default function Loader({ onDone }) {
  const [phase, setPhase] = useState('enter');   // enter → hold → exit
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Progress bar ticks
    const tick = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(tick); return 100; }
        return p + (p < 70 ? 2.2 : p < 90 ? 1.1 : 0.6);
      });
    }, 30);

    // Start exit at 1.6s
    const exitTimer = setTimeout(() => setPhase('exit'), 1600);

    // Unmount at 2.3s
    const doneTimer = setTimeout(() => onDone(), 2300);

    return () => {
      clearInterval(tick);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300&display=swap');

        .vr-loader {
          position: fixed; inset: 0; z-index: 99999;
          background: #080808;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .vr-loader.exit {
          opacity: 0;
          transform: translateY(-24px);
        }

        /* VR letters */
        .vr-letters {
          position: relative;
          display: flex; align-items: flex-end; gap: 4px;
          margin-bottom: 48px;
        }

        .vr-letter {
          font-family: 'Cormorant Garamond', serif;
          font-size: 96px;
          font-weight: 300;
          line-height: 1;
          color: transparent;
          background: linear-gradient(
            135deg,
            #6b5a3a 0%,
            #c9a96e 30%,
            #f0d898 50%,
            #c9a96e 70%,
            #6b5a3a 100%
          );
          background-size: 200% 200%;
          -webkit-background-clip: text;
          background-clip: text;
          animation: goldShimmer 2s linear infinite;
          opacity: 0;
          transform: translateY(20px);
        }
        .vr-letter.v {
          animation: goldShimmer 2s linear infinite, letterIn 0.55s 0.1s ease forwards;
        }
        .vr-letter.r {
          animation: goldShimmer 2s linear infinite, letterIn 0.55s 0.25s ease forwards;
          font-style: italic;
        }

        @keyframes goldShimmer {
          0%   { background-position: 200% 0%; }
          100% { background-position: -200% 0%; }
        }
        @keyframes letterIn {
          to { opacity: 1; transform: translateY(0); }
        }

        /* Dot separator */
        .vr-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #c9a96e;
          margin-bottom: 18px;
          opacity: 0;
          animation: dotPop 0.4s 0.45s ease forwards;
        }
        @keyframes dotPop {
          0%   { opacity: 0; transform: scale(0); }
          70%  { transform: scale(1.3); }
          100% { opacity: 1; transform: scale(1); }
        }

        /* Decorative lines */
        .vr-lines {
          position: absolute;
          inset: -24px;
          pointer-events: none;
        }
        .vr-line {
          position: absolute;
          background: linear-gradient(to right, transparent, #c9a96e, transparent);
          opacity: 0;
        }
        .vr-line.top    { top: 0; left: 10%; right: 10%; height: 1px; animation: lineIn 0.5s 0.5s ease forwards; }
        .vr-line.bottom { bottom: 0; left: 10%; right: 10%; height: 1px; animation: lineIn 0.5s 0.6s ease forwards; }
        @keyframes lineIn {
          from { opacity: 0; transform: scaleX(0); }
          to   { opacity: 0.5; transform: scaleX(1); }
        }

        /* Progress bar */
        .vr-progress-wrap {
          width: 160px; height: 1px;
          background: rgba(201,169,110,0.12);
          position: relative; overflow: hidden;
          opacity: 0;
          animation: fadeIn 0.4s 0.6s ease forwards;
        }
        .vr-progress-fill {
          position: absolute; top: 0; left: 0; height: 100%;
          background: linear-gradient(to right, #6b5a3a, #c9a96e, #f0d898);
          transition: width 0.08s linear;
          box-shadow: 0 0 8px rgba(201,169,110,0.6);
        }

        /* Tagline */
        .vr-tag {
          font-family: 'Syne', sans-serif;
          font-size: 9px;
          letter-spacing: 0.4em;
          text-transform: uppercase;
          color: rgba(201,169,110,0.4);
          margin-bottom: 32px;
          opacity: 0;
          animation: fadeIn 0.5s 0.55s ease forwards;
        }

        @keyframes fadeIn {
          to { opacity: 1; }
        }

        /* Corner ornaments */
        .vr-corner {
          position: absolute;
          width: 20px; height: 20px;
          opacity: 0;
          animation: cornerIn 0.5s 0.7s ease forwards;
        }
        .vr-corner.tl { top: 32px; left: 32px; border-top: 1px solid rgba(201,169,110,0.4); border-left: 1px solid rgba(201,169,110,0.4); }
        .vr-corner.tr { top: 32px; right: 32px; border-top: 1px solid rgba(201,169,110,0.4); border-right: 1px solid rgba(201,169,110,0.4); }
        .vr-corner.bl { bottom: 32px; left: 32px; border-bottom: 1px solid rgba(201,169,110,0.4); border-left: 1px solid rgba(201,169,110,0.4); }
        .vr-corner.br { bottom: 32px; right: 32px; border-bottom: 1px solid rgba(201,169,110,0.4); border-right: 1px solid rgba(201,169,110,0.4); }
        @keyframes cornerIn {
          from { opacity: 0; transform: scale(0.5); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div className={`vr-loader${phase === 'exit' ? ' exit' : ''}`}>
        {/* Corner ornaments */}
        <div className="vr-corner tl" />
        <div className="vr-corner tr" />
        <div className="vr-corner bl" />
        <div className="vr-corner br" />

        {/* VR mark */}
        <div className="vr-letters">
          <div className="vr-lines">
            <div className="vr-line top" />
            <div className="vr-line bottom" />
          </div>
          <span className="vr-letter v">V</span>
          
          <span className="vr-letter r">R</span>
        </div>

        {/* Tagline */}
        <div className="vr-tag">Vimal Raj &nbsp;·&nbsp; Developer</div>

        {/* Progress */}
        <div className="vr-progress-wrap">
          <div className="vr-progress-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      </div>
    </>
  );
}
